/**
 * NotificationBell Widget
 *
 * Main notification bell component with badge and dropdown.
 *
 * Features:
 * - Bell icon with unread count badge
 * - Real-time updates via WebSocket (useNotifications hook)
 * - Dropdown with notifications list
 * - Click-outside detection to close dropdown
 * - Responsive design (hidden on mobile < lg)
 *
 * Usage:
 * <NotificationBell locale="en" />
 */

'use client';

import { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { cn } from '@/src/shared/lib/utils';
import { useNotifications } from '@/src/shared/hooks/useNotifications';
import { NotificationDropdown } from './ui/NotificationDropdown';

interface NotificationBellProps {
  locale: string;
}

export function NotificationBell({ locale }: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get real-time unread count from WebSocket
  // Note: showToasts is disabled here to avoid duplicate toasts
  // NotificationProvider already handles toast notifications
  const { unreadCount, isConnected } = useNotifications({ showToasts: false });

  // Click-outside detection
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle "mark all as read" from dropdown
  const handleMarkAllAsRead = () => {
    // WebSocket will automatically update unreadCount via notification:unread-count event
    // No need to manually update count here
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'relative flex items-center justify-center w-10 h-10 rounded-lg',
          'text-text-primary hover:bg-background-2 transition-colors',
          isOpen && 'bg-background-2'
        )}
        type="button"
        aria-label="Notifications"
      >
        <Bell className="h-6 w-6" />

        {/* Unread indicator - red dot */}
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-3 h-3 bg-red-600 rounded-full border-2 border-white" />
        )}

        {/* Connection indicator (optional, for debugging) */}
        {/* {!isConnected && (
          <span className="absolute bottom-0 right-0 w-2 h-2 bg-gray-400 rounded-full" />
        )} */}
      </button>

      {/* Dropdown */}
      <NotificationDropdown
        isOpen={isOpen}
        locale={locale}
        onClose={() => setIsOpen(false)}
        onMarkAllAsRead={handleMarkAllAsRead}
      />
    </div>
  );
}
